import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

async function authClient() {
  let token = '';
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
    } catch {
      /* ignore */
    }
  }
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  });
}

export type AccountingProvider = 'xero' | 'quickbooks';

export interface AccountingSyncQueueSummary {
  pending: number;
  failed: number;
  lastFailureAt: string | null;
}

export interface AccountingIntegrationStatusResponse {
  status: string;
  provider: AccountingProvider | null;
  platform: string | null;
  platformName: string | null;
  lastSyncedAt: string | null;
  lastError: { code: string | null; message: string | null; at: string | null } | null;
  connectedAt: string | null;
  syncQueue: AccountingSyncQueueSummary;
  defaultPaymentAccountId: string | null;
  defaultSalesAccountId: string | null;
  defaultTaxCodeId: string | null;
  invoicePostingMode: AccountingInvoicePostingMode;
  paymentSyncMode: AccountingPaymentSyncMode;
}

export type AccountingInvoicePostingMode = 'draft' | 'submitted' | 'authorised';
export type AccountingPaymentSyncMode = 'immediate' | 'invoice_only';

export async function fetchAccountingIntegration(
  academyId: string
): Promise<AccountingIntegrationStatusResponse> {
  const client = await authClient();
  const res = await client.get<{ success: boolean; data: AccountingIntegrationStatusResponse }>(
    '/accounting/integration',
    { params: { academyId } }
  );
  return res.data.data;
}

export async function startAccountingOAuth(
  academyId: string,
  provider: AccountingProvider
): Promise<{ authorizationUrl: string }> {
  const client = await authClient();
  const res = await client.post<{
    success: boolean;
    data: { authorizationUrl: string };
  }>('/accounting/oauth/start', { academyId, provider });
  return res.data.data;
}

export interface DisconnectAccountingIntegrationResult {
  disconnected: boolean;
  remoteRevoked: boolean;
  hadStoredCredentials: boolean;
  message?: string;
}

export async function disconnectAccountingIntegration(
  academyId: string
): Promise<DisconnectAccountingIntegrationResult> {
  const client = await authClient();
  const res = await client.delete<{
    success: boolean;
    data: DisconnectAccountingIntegrationResult;
  }>('/accounting/integration', { params: { academyId } });
  return res.data.data;
}

export interface AccountingPostingCatalogAccount {
  id: string;
  name: string | null;
  accountType: string | null;
  classification: string | null;
}

export interface AccountingPostingCatalogTaxRate {
  id: string;
  name: string | null;
  totalTaxRate: number | null;
}

export interface AccountingPostingCatalogResponse {
  accounts: AccountingPostingCatalogAccount[];
  taxRates: AccountingPostingCatalogTaxRate[];
}

export async function fetchAccountingPostingCatalog(
  academyId: string
): Promise<AccountingPostingCatalogResponse> {
  const client = await authClient();
  const res = await client.get<{ success: boolean; data: AccountingPostingCatalogResponse }>(
    '/accounting/posting-catalog',
    { params: { academyId } }
  );
  return res.data.data;
}

export interface PatchAccountingPostingDefaultsResult {
  updated: boolean;
  defaultPaymentAccountId: string | null;
  defaultSalesAccountId: string | null;
  defaultTaxCodeId: string | null;
  invoicePostingMode: AccountingInvoicePostingMode;
  paymentSyncMode: AccountingPaymentSyncMode;
}

export async function patchAccountingPostingDefaults(
  academyId: string,
  data: {
    defaultPaymentAccountId?: string | null;
    defaultSalesAccountId?: string | null;
    defaultTaxCodeId?: string | null;
    invoicePostingMode?: AccountingInvoicePostingMode;
    paymentSyncMode?: AccountingPaymentSyncMode;
  }
): Promise<PatchAccountingPostingDefaultsResult> {
  const client = await authClient();
  const res = await client.patch<{ success: boolean; data: PatchAccountingPostingDefaultsResult }>(
    '/accounting/posting-defaults',
    { academyId, ...data }
  );
  return res.data.data;
}
